import {sleep} from '@benchling/js-utils';

import {assertAndContinue} from 'Asserts';
import {getAutobatcher, ModelFetcher, requestFetch, requestFetchOptions} from 'models/Autobatcher';
import {InaccessibleModelSelector} from 'models/reduceInaccessibleModels';
import {getLoadingState, LoadingState} from 'models/reduceLoadingState';
import nextReduxAction from 'redux-stores/nextReduxAction';
import {BenchlingStore} from 'redux-stores/Store';
import {expectNotImporting} from 'util/ImportTracker';
import * as net from 'util/Net';
import {INACCESSIBLE} from 'util/Types';

import {loadingCompleted, loadingFailed, loadingStarted} from '../LoadingStateReduxActions';
import {ManagedModelType, ManagedModelTypes} from '../ManagedModelDefinitions';

export const GET_MODEL_TIMEOUT_MS = 120000;

/**
 * Lazy-import the redux store to break the import-time dependency from ModelManager to the store (which in
 * turn imports many other things).
 */
function getStore(): BenchlingStore {
  // eslint-disable-next-line global-require
  const StoreModule = require('redux-stores/Store') as typeof import('redux-stores/Store');
  return StoreModule.getStore();
}

type Model<T extends ManagedModelType> = ManagedModelTypes[T] | INACCESSIBLE;

/**
 * A ModelFetcher that supports getOrFetchRequired()
 *
 * This works in conjunction with the Autobatcher, which handles network-level request deduplication. The
 * division of responsibilities is:
 * - Autobatcher: Ensures only one network request per model key per batch
 * - ModelManager: Manages model state in the store (loading/error states and model data) and ensures
 *   multiple callers share the same Promise to prevent duplicate loading states and timeout errors
 */
export abstract class ModelManager<T extends ManagedModelType> implements ModelFetcher {
  modelType: T;

  /**
   * Promise cache for in-flight model fetching requests. This provides application-level promise sharing to
   * prevent duplicate timeout errors when multiple call sites request the same model simultaneously.
   * The cache key includes both the model key and relevant fetch options to ensure correct promise sharing.
   */
  private pendingPromises = new Map<string, Promise<Model<T>>>();

  constructor(modelType: T) {
    expectNotImporting();
    this.modelType = modelType;
  }

  isKeyLoadedOrLoading(key: string): boolean {
    const isModelInStore = Boolean(this.getModel(key));
    const loadingState = getStore().getState().models.loadingState;
    const isLoading = loadingState[this.modelType] && Boolean(loadingState[this.modelType][key]);
    return isModelInStore || isLoading;
  }

  async fetchMulti(keys: Array<string>): Promise<void> {
    try {
      getStore().dispatch(loadingStarted(this.modelType, keys));
      await this.doFetchMulti(keys);
      getStore().dispatch(loadingCompleted(this.modelType, keys));
      const unfetchedKeys = keys.filter(
        (key) => this.getModel(key) == null && !this.isModelInaccessible(key)
      );
      assertAndContinue(
        unfetchedKeys.length === 0,
        `Expected all the models to exist for ${this.modelType} after fetching is completed.`,
        {unfetchedKeys}
      );
    } catch (e) {
      if (net.isServerRequestError(e)) {
        getStore().dispatch(loadingFailed(this.modelType, keys));
      }
      throw e;
    }
  }

  abstract doFetchMulti(keys: Array<string>): Promise<void>;

  abstract getModel(modelKey: string): ManagedModelTypes[T] | null;

  /**
   * Model managers can specify where a given model key is inaccessible. This supports model managers whose
   * inaccesible state is not in models.inaccessibleState (e.g. models.inaccessibleFileIds), though ideally
   * all inaccessible state would live together.
   */
  isModelInaccessible(modelKey: string): boolean {
    return (
      new InaccessibleModelSelector(getStore().getState()).getInaccessibleInfo(this.modelType, modelKey) !=
      null
    );
  }

  /**
   * Either fetches a model from the store and returns it (issuing a network request to load it if necessary),
   * or returns INACCESSIBLE if the user does not have access to the model.
   */
  fetchModelMaybeInaccessible(
    modelKey: string,
    fetchOptions: requestFetchOptions = {forceFetch: false}
  ): Promise<Model<T>> {
    const cacheKey = `${modelKey}:${JSON.stringify(fetchOptions)}`;
    const existingPromise = this.pendingPromises.get(cacheKey);
    if (existingPromise) {
      return existingPromise;
    }

    // Request the model through the autobatcher (handles network-level deduplication)
    requestFetch(this, modelKey, fetchOptions);

    const newPromise = this.waitForModelToLoad(modelKey, Number(new Date()), fetchOptions);

    const promiseWithCleanup = newPromise.finally(() => {
      this.pendingPromises.delete(cacheKey);
    });
    this.pendingPromises.set(cacheKey, promiseWithCleanup);
    return promiseWithCleanup;
  }

  /**
   * Fetches a model and returns it, throwing an exception if the user does not have access to the model.
   *
   * This serves the same purpose as DataConnector.getOrFetchRequired, but for cases where we want to
   * fetch a model within an async function rather than in the more specific, limited
   * context of a data connector.
   */
  async fetchModel(key: string, fetchOptions?: requestFetchOptions): Promise<ManagedModelTypes[T]> {
    const model = await this.fetchModelMaybeInaccessible(key, fetchOptions);
    if (model === INACCESSIBLE) {
      throw new Error(`User does not have access to model ${this.modelType} with key ${key}`);
    }
    return model;
  }

  /**
   * Fetches multiple models and returns them as an array.
   */
  async fetchModelList(
    keys: Array<string>,
    fetchOptions?: requestFetchOptions
  ): Promise<Array<ManagedModelTypes[T]>> {
    return await Promise.all(keys.map((k) => this.fetchModel(k, fetchOptions)));
  }

  /**
   * Waits for a model to load, handling timeouts and error states.
   */
  private async waitForModelToLoad(
    key: string,
    start: number,
    fetchOptions?: requestFetchOptions
  ): Promise<Model<T>> {
    let model = this.getModel(key);

    while (!model || (fetchOptions?.forceFetch && getAutobatcher().isFetchPendingOrActive(this, key))) {
      if (fetchOptions?.forceFetch) {
        // isFetchPendingOrActive is updated async, so it may fall after the last `nextReduxAction` - so instead
        // we switch to a less performant, but guaranteed polling mechanism.
        await sleep(0.1);
      } else {
        await nextReduxAction();
      }
      if (Number(new Date()) - start > GET_MODEL_TIMEOUT_MS) {
        // The model manager does not handle timeouts (data connectors will keep loading indefinitely).
        throw new Error(
          `Timeout of ${GET_MODEL_TIMEOUT_MS} ms exceeded in fetching model type ${this.modelType} with key ${key}`
        );
      }
      if (
        getLoadingState(getStore().getState().models.loadingState, this.modelType, key) ===
        LoadingState.FAILED
      ) {
        throw new Error(`Loading failed for model ${this.modelType} with key ${key}`);
      }
      if (this.isModelInaccessible(key)) {
        return INACCESSIBLE;
      }
      model = this.getModel(key);
    }
    return model;
  }
}
